import { Cloudinary } from "@cloudinary/url-gen";
import { cloudinaryConfig } from "../config/cloudinary";

// Mobile-only image asset interface (for React Native)
interface MobileImageAsset {
  uri: string;
  type?: string;
  width?: number;
  height?: number;
}

// Initialize Cloudinary instance
const cld = new Cloudinary({
  cloud: {
    cloudName: cloudinaryConfig.cloudName,
  },
  url: {
    secure: true,
  },
});

export interface ImageUploadResult {
  url: string;
  publicId: string;
  filename: string;
  transformedUrl?: string; // URL with any transformations applied
}

/**
 * Upload image to Cloudinary (Mobile/React Native only)
 * For web uploads, use the Cloudinary Upload Widget instead
 */
export const uploadImageToCloudinary = async (
  image: MobileImageAsset,
  folder: string = "hole-in-one"
): Promise<ImageUploadResult> => {
  try {
    console.log("🔍 Cloudinary config:", {
      cloudName: cloudinaryConfig.cloudName,
      uploadPreset: cloudinaryConfig.uploadPreset,
      apiUrl: cloudinaryConfig.apiUrl,
    });

    const formData = new FormData();

    // Create a unique filename
    const timestamp = Date.now();
    const filename = `${folder}_${timestamp}.jpg`;

    // Mobile: URI is a string path
    formData.append("file", {
      uri: image.uri,
      type: image.type || "image/jpeg",
      name: filename,
    } as any);

    formData.append("upload_preset", cloudinaryConfig.uploadPreset);
    formData.append("folder", folder);

    console.log("📤 Uploading to Cloudinary (Mobile):", {
      preset: cloudinaryConfig.uploadPreset,
      folder,
      filename,
      url: cloudinaryConfig.apiUrl,
    });

    const response = await fetch(cloudinaryConfig.apiUrl, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ Cloudinary upload failed:", {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });
      throw new Error(`Upload failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log("✅ Cloudinary upload successful:", {
      url: result.secure_url,
      publicId: result.public_id,
    });

    // Generate optimized URL using the modern SDK
    const optimizedImage = cld.image(result.public_id).format("auto").quality("auto");

    return {
      url: result.secure_url,
      publicId: result.public_id,
      filename: result.original_filename,
      transformedUrl: optimizedImage.toURL(),
    };
  } catch (error) {
    console.error("💥 Error uploading image to Cloudinary:", error);
    throw error;
  }
};

/**
 * Upload multiple images to Cloudinary (Mobile/React Native only)
 * For web uploads, use the Cloudinary Upload Widget instead
 */
export const uploadMultipleImages = async (
  images: MobileImageAsset[],
  folder: string = "hole-in-one"
): Promise<ImageUploadResult[]> => {
  const uploadPromises = images.map((image) => uploadImageToCloudinary(image, folder));
  return Promise.all(uploadPromises);
};

// Helper function to generate optimized image URLs
export const getOptimizedImageUrl = (publicId: string, transformations?: any) => {
  let image = cld.image(publicId).format("auto").quality("auto");

  // Apply any additional transformations if provided
  if (transformations) {
    image = image.addTransformation(transformations);
  }

  return image.toURL();
};
